import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { AppUser } from "../../services/userService";
import type { NoteRefDto } from "../../services/vorgangService";
import { activePersonToken, effectiveMentions, mentionToken } from "../notes/mentions";
import { AuswahlAnker } from "./AuswahlAnker";
import { BezugPicker } from "./BezugPicker";
import { MentionPicker } from "./MentionPicker";
import { behandleAuswahlTastatur } from "./auswahlTastatur";
import type { BezugKandidat } from "./bezugKandidaten";
import { bezugImText, refsImText, type KommentarAnhang, type KommentarZusatz } from "./kommentarInhalt";
import { DateiTypPlakette } from "../documents/DateiTypSymbol";
import { DiktatKnopf } from "../diktat/DiktatKnopf";
import { textareaEinsetzen } from "../diktat/diktatEinfuegen";
import "./notizbloecke.css";
import "./kommentare.css";

/**
 * DAS SCHREIBFELD EINES KOMMENTARS — mit `@`, `#` und Anhang (PO 28.09.2026:
 * „Erwähnen und # im Kommentar funktioniert nicht", „im Kommentar einen
 * Anhang hinzufügen, Bild, Dok, …").
 *
 * Bis hierher war es ein nacktes `textarea`: `@kürzel` zählte nur, wenn man
 * das Kürzel auswendig wusste (`resolveTypedMentions`), und hinter `#` kam gar
 * nichts. Jetzt öffnen beide dieselben Auswahllisten wie im Notizfeld
 * (`MentionPicker`, `BezugPicker`) — an dieselbe Stelle gehängt
 * (`AuswahlAnker`), mit derselben `mousedown`-Regel, damit der Klick in die
 * Liste dem Feld den Cursor nicht nimmt.
 *
 * EIN Baustein für alle drei Orte, an denen kommentiert wird: die Spalte neben
 * der Verlaufskarte, der Faden am Notizabschnitt und der Faden an einer
 * markierten Textstelle. Vorher stand dasselbe `textarea` dreimal da, und
 * jede Verbesserung hätte dreimal gemacht werden müssen.
 *
 * Was am Ende gespeichert wird, entscheidet `kommentarInhalt.ts`.
 */

export type KommentarEingabeStart = {
    text: string;
    refs?: NoteRefDto[];
    anhaenge?: KommentarAnhang[];
};

export type KommentarSenden = (
    text: string,
    mentions: string[],
    zusatz?: KommentarZusatz,
) => Promise<void>;

type Props = {
    users: AppUser[];
    /** Das Angebot hinter `#`; fehlt es, bleibt `#` ein gewöhnliches Zeichen. */
    kandidaten?: BezugKandidat[];
    /** Optik: neben der Verlaufskarte (`vg4-ks-*`) oder im Faden (`nb-kf-*`). */
    stil: "spalte" | "faden";
    start?: KommentarEingabeStart;
    placeholder?: string;
    ariaLabel: string;
    sendenLabel?: string;
    autoFocus?: boolean;
    /**
     * Dateien anhängen erlaubt? Ohne Angabe: NEIN — nur wer `zusatz` beim
     * Speichern auch auswertet, darf den Knopf zeigen. Sonst gingen Anhänge
     * still verloren (etwa im Kundentagebuch, das denselben Faden benutzt).
     */
    mitAnhang?: boolean;
    onSenden: KommentarSenden;
    /**
     * Darf LEER abgeschickt werden? Beim Ändern ja: „leergeräumt heißt
     * gelöscht" (PO 13.09.2026) — das entscheidet der Aufrufer.
     */
    leerErlaubt?: boolean;
    /** Fehlt er, gibt es keinen Abbrechen-Knopf (der Faden bleibt offen). */
    onAbbrechen?: () => void;
};

/** Welche Liste gerade offen ist — und ab welcher Stelle ersetzt wird. */
type Token = { trigger: "@" | "#"; start: number; query: string };

const Klammer: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M15.5 9.2 9.6 15.1a3.6 3.6 0 0 1-5.1-5.1l6.3-6.3a2.4 2.4 0 0 1 3.4 3.4l-6.2 6.2a1.2 1.2 0 0 1-1.7-1.7l5.6-5.6" />
    </svg>
);

/** Ist die Datei ein Bild, das als Notizbild im Text stehen kann? */
export const istBildDatei = (f: File): boolean => /^image\/(png|jpe?g|gif|webp)$/i.test(f.type);

/**
 * Den Auswahltext an die Stelle des getippten Tokens setzen — `@mm ` bzw.
 * `#Titel `. Rein, damit es ohne Browser prüfbar ist.
 */
export function tokenErsetzenImText(
    text: string,
    token: { start: number },
    caret: number,
    einsatz: string,
): { text: string; caret: number } {
    const vor = text.slice(0, token.start);
    const nach = text.slice(caret).replace(/^\S*/, "");
    const neu = `${vor}${einsatz} ${nach.replace(/^ /, "")}`;
    return { text: neu, caret: vor.length + einsatz.length + 1 };
}

export const KommentarEingabe: React.FC<Props> = ({
    users,
    kandidaten,
    stil,
    start,
    placeholder,
    ariaLabel,
    sendenLabel = "Kommentieren",
    autoFocus,
    mitAnhang = false,
    onSenden,
    leerErlaubt = false,
    onAbbrechen,
}) => {
    const [text, setText] = useState(start?.text ?? "");
    const [refs, setRefs] = useState<NoteRefDto[]>(start?.refs ?? []);
    const [anhaenge, setAnhaenge] = useState<KommentarAnhang[]>(start?.anhaenge ?? []);
    const [dateien, setDateien] = useState<File[]>([]);
    const [gewaehlt, setGewaehlt] = useState<string[]>([]);
    const [token, setToken] = useState<Token | null>(null);
    /** Der per ↑/↓ markierte Vorschlag der offenen Auswahl (T-50). */
    const [aktiv, setAktiv] = useState(0);
    useEffect(() => setAktiv(0), [token?.trigger, token?.query]);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const feldRef = useRef<HTMLTextAreaElement | null>(null);
    const dateiRef = useRef<HTMLInputElement | null>(null);
    /** Wohin der Cursor nach dem Einsetzen soll — gesetzt NACH dem Rendern. */
    const caretRef = useRef<number | null>(null);
    /** Die Auswahl beim Start eines Diktats — dort landet der Text. */
    const diktatStelleRef = useRef<{ start: number; ende: number } | null>(null);

    useLayoutEffect(() => {
        const el = feldRef.current;
        if (el && caretRef.current != null) {
            el.focus();
            el.setSelectionRange(caretRef.current, caretRef.current);
            caretRef.current = null;
        }
    });

    const k = stil === "spalte" ? "vg4-ks" : "nb-kf";
    const mitHash = !!kandidaten?.length;

    const tokenPruefen = (wert: string, caret: number) => {
        const t = activePersonToken(wert, caret, mitHash);
        if (!t || (t.trigger === "@" && !users.length)) {
            setToken(null);
            return;
        }
        setToken({ trigger: t.trigger, start: t.start, query: t.query });
    };

    const einsetzen = (einsatz: string) => {
        const el = feldRef.current;
        if (!token || !el) return;
        const r = tokenErsetzenImText(text, token, el.selectionStart ?? text.length, einsatz);
        caretRef.current = r.caret;
        setText(r.text);
        setToken(null);
    };

    const erwaehnen = (u: AppUser) => {
        einsetzen(`@${mentionToken(u)}`);
        setGewaehlt((g) => (g.includes(u.id) ? g : [...g, u.id]));
    };

    const bezugWaehlen = (kand: BezugKandidat) => {
        const ref: NoteRefDto = { art: kand.art, id: kand.id, label: kand.titel };
        einsetzen(bezugImText(ref));
        setRefs((rs) =>
            rs.some((r) => r.art === ref.art && String(r.id) === String(ref.id)) ? rs : [...rs, ref],
        );
    };

    const dateienDazu = (liste: FileList | File[] | null | undefined) => {
        const neu = Array.from(liste ?? []);
        if (!neu.length) return;
        setDateien((d) => [...d, ...neu]);
    };

    const diktatMerken = () => {
        const el = feldRef.current;
        diktatStelleRef.current = el
            ? { start: el.selectionStart ?? text.length, ende: el.selectionEnd ?? text.length }
            : null;
    };

    const diktatEinsetzen = (diktiert: string) => {
        const stelle = diktatStelleRef.current ?? { start: text.length, ende: text.length };
        diktatStelleRef.current = null;
        const r = textareaEinsetzen(text, stelle.start, stelle.ende, diktiert);
        caretRef.current = r.caret;
        setText(r.wert);
        setToken(null);
    };

    const hatInhalt = !!text.trim() || anhaenge.length > 0 || dateien.length > 0;

    const senden = async () => {
        if ((!hatInhalt && !leerErlaubt) || busy) return;
        setBusy(true);
        setFehler(null);
        try {
            const inhalt = text.trim();
            const zusatzNoetig =
                dateien.length > 0 || anhaenge.length > 0 || refsImText(inhalt, refs).length > 0;
            // Die Erwähnungen aus dem TEXT: gewählt UND noch dastehend, dazu
            // frei getippte `@kürzel` — dieselbe Regel wie im Notiz-Chat.
            const mentions = effectiveMentions(inhalt, gewaehlt, users);
            await (zusatzNoetig
                ? onSenden(inhalt, mentions, { refs, dateien, anhaenge })
                : onSenden(inhalt, mentions));
            setText(start?.text ?? "");
            setRefs(start?.refs ?? []);
            setAnhaenge(start?.anhaenge ?? []);
            setDateien([]);
            setGewaehlt([]);
        } catch (err: any) {
            console.error("[KommentarEingabe] Kommentar speichern fehlgeschlagen", err);
            setFehler(err?.message ?? "Kommentar konnte nicht gespeichert werden.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`nb-ke nb-ke-${stil}`}>
            <textarea
                ref={feldRef}
                className={`${k}-feld`}
                rows={2}
                // Textfeld: hier prüft der Browser (Edge → Windows-Rechtschreibung) auf Deutsch.
                spellCheck
                lang="de"
                value={text}
                placeholder={
                    placeholder ??
                    (mitHash
                        ? "Kommentieren … @ erwähnt, # verweist"
                        : "Kommentieren … @ erwähnt jemanden")
                }
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                disabled={busy}
                onChange={(e) => {
                    setText(e.target.value);
                    tokenPruefen(e.target.value, e.target.selectionStart ?? e.target.value.length);
                }}
                onSelect={(e) => {
                    const el = e.currentTarget;
                    tokenPruefen(el.value, el.selectionStart ?? el.value.length);
                }}
                onBlur={() => setToken(null)}
                onPaste={(e) => {
                    if (!mitAnhang) return;
                    const files = Array.from(e.clipboardData?.files ?? []);
                    if (!files.length) return;
                    e.preventDefault();
                    dateienDazu(files);
                }}
                // Mit Anhang nimmt das Feld JEDE Datei selbst — die Karte darum
                // zeigt dann nicht ihr „Dateien an … hochladen" (`eigeneAblage.ts`).
                {...(mitAnhang ? { "data-eigene-ablage": "dateien" } : {})}
                onDragOver={(e) => {
                    if (mitAnhang && Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
                        e.preventDefault();
                    }
                }}
                onDrop={(e) => {
                    if (!mitAnhang || !e.dataTransfer?.files?.length) return;
                    e.preventDefault();
                    dateienDazu(e.dataTransfer.files);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (token) setToken(null);
                        else onAbbrechen?.();
                        return;
                    }
                    // Offene `@`-/`#`-Auswahl: ↑/↓ wechseln, Enter/Tab
                    // übernehmen den markierten Vorschlag (T-50). Strg/⌘+Enter
                    // gehört weiter dem Senden.
                    if (
                        behandleAuswahlTastatur(
                            e,
                            token ? { ausloeser: token.trigger, query: token.query } : null,
                            { users, kandidaten },
                            aktiv,
                            { setAktiv, erwaehnen, waehlen: bezugWaehlen },
                        )
                    )
                        return;
                    // Strg/⌘ + Enter schickt ab; ein bloßes Enter bleibt der
                    // Zeilenumbruch — ein Kommentar darf mehr als einen Satz
                    // haben.
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void senden();
                    }
                }}
            />

            {anhaenge.length || dateien.length ? (
                <ul className="nb-ke-anhaenge" aria-label="Anhänge">
                    {anhaenge.map((a) => (
                        <li key={`a${a.id}`} className="nb-ke-anhang">
                            <DateiTypPlakette fileName={a.name} mimeType={a.bild ? "image/*" : null} />
                            <span className="nb-ke-anhang-name">{a.name}</span>
                            <button
                                type="button"
                                className="nb-ke-anhang-weg"
                                aria-label={`${a.name} entfernen`}
                                disabled={busy}
                                onClick={() => setAnhaenge((l) => l.filter((x) => x !== a))}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                    {dateien.map((d, i) => (
                        <li key={`d${i}-${d.name}`} className="nb-ke-anhang">
                            <DateiTypPlakette fileName={d.name} mimeType={d.type} />
                            <span className="nb-ke-anhang-name">{d.name}</span>
                            <button
                                type="button"
                                className="nb-ke-anhang-weg"
                                aria-label={`${d.name} entfernen`}
                                disabled={busy}
                                onClick={() => setDateien((l) => l.filter((x) => x !== d))}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}

            {fehler ? <div className={`${k}-fehler`}>{fehler}</div> : null}

            <div className={`${k}-fuss`}>
                <button
                    type="button"
                    className={`${k}-senden`}
                    disabled={busy || (!hatInhalt && !leerErlaubt)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void senden()}
                >
                    {busy ? "Speichert…" : sendenLabel}
                </button>
                {onAbbrechen ? (
                    <button
                        type="button"
                        className={`${k}-tun-btn`}
                        disabled={busy}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onAbbrechen}
                    >
                        Abbrechen
                    </button>
                ) : null}
                <DiktatKnopf
                    className="nb-ke-diktat"
                    disabled={busy}
                    onStart={diktatMerken}
                    onText={diktatEinsetzen}
                />
                {mitAnhang ? (
                    <>
                        <button
                            type="button"
                            className="nb-ke-anhang-btn"
                            title="Bild oder Dokument anhängen"
                            aria-label="Anhang hinzufügen"
                            disabled={busy}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => dateiRef.current?.click()}
                        >
                            <Klammer />
                            Anhang
                        </button>
                        <input
                            ref={dateiRef}
                            type="file"
                            multiple
                            hidden
                            aria-hidden
                            tabIndex={-1}
                            onChange={(e) => {
                                dateienDazu(e.target.files);
                                e.target.value = "";
                            }}
                        />
                    </>
                ) : null}
            </div>

            {token?.trigger === "@" ? (
                <AuswahlAnker feld={feldRef.current}>
                    <MentionPicker
                        users={users}
                        query={token.query}
                        onWaehlen={erwaehnen}
                        onAbbrechen={() => setToken(null)}
                        aktiv={aktiv}
                        onAktiv={setAktiv}
                    />
                </AuswahlAnker>
            ) : null}
            {token?.trigger === "#" && kandidaten?.length ? (
                <AuswahlAnker feld={feldRef.current}>
                    <BezugPicker
                        kandidaten={kandidaten}
                        query={token.query}
                        onWaehlen={bezugWaehlen}
                        onAbbrechen={() => setToken(null)}
                        aktiv={aktiv}
                        onAktiv={setAktiv}
                    />
                </AuswahlAnker>
            ) : null}
        </div>
    );
};
